/**
 * ghosts.ts - Ghost character definitions
 */

import type { FearType } from './rooms.js';

export type GhostGrade = 'C' | 'B' | 'A' | 'S';
export type GhostId =
  | 'jinaeryeong'
  | 'gangsi'
  | 'vampire'
  | 'kappa'
  | 'pierrot'
  | 'banshee'
  | 'mummy'
  | 'werewolf'
  | 'grim_reaper'
  | 'blood_count'
  | 'well_spirit'
  | 'nightmare_specter'
  | 'shadow_stalker';

export interface GhostStatBonus {
  fear: number;
  thrill: number;
  atmosphere: number;
  fun: number;
}

/** Active skill effect types */
export type SkillTrigger =
  | 'on_scare'           // triggers every scare
  | 'on_chain'           // triggers when chain count > 0
  | 'on_low_condition'   // triggers when room condition < 30
  | 'on_high_fatigue'    // triggers when ghost fatigue > 60
  | 'on_first_visit';    // triggers on the first room in visitor path

export interface GhostActiveSkill {
  trigger: SkillTrigger;
  /** Multiplier bonus when skill activates (e.g. 0.2 = +20%) */
  bonus: number;
  /** Chance to activate (0-1). 1 = always */
  chance: number;
}

export interface GhostDef {
  id: GhostId;
  nameKey: string;
  originKey: string;
  grade: GhostGrade;
  fearTypes: FearType[];
  baseFear: number;       // base scare power
  hireCost: number;       // SP cost
  skillKey: string;       // skill description i18n key
  quoteKey: string;       // idle quote i18n key
  fatiguedQuoteKey: string; // fatigued quote i18n key
  image: string;           // character image path (idle)
  hideImage: string;       // hiding pose image path
  scareImage: string;      // scaring pose image path
  statBonus: GhostStatBonus; // emotion stat bonuses when assigned to a room
  activeSkill: GhostActiveSkill;
  themeTags: string[];
  artProfileId: string;
  artNotes: string;
  isPro?: boolean;         // true for professional ghosts
  startingSkillLevel?: number; // starting academy skill level (pro only)
  uniqueAbilityKey?: string;   // unique ability (pro only)
}

export const GHOST_DEFS: Record<GhostId, GhostDef> = {
  jinaeryeong: {
    id: 'jinaeryeong',
    nameKey: 'ghost_jinaeryeong',
    originKey: 'ghost_origin_korea',
    grade: 'B',
    fearTypes: ['visual', 'psychological'],
    baseFear: 30,
    hireCost: 500,
    skillKey: 'ghost_jinaeryeong_skill',
    quoteKey: 'ghost_jinaeryeong_quote',
    fatiguedQuoteKey: 'ghost_jinaeryeong_fatigued',
    image: '/images/ghost/virgin-idle.webp',
    hideImage: '/images/ghost/virgin-hide.webp',
    scareImage: '/images/ghost/virgin-scare.webp',
    statBonus: { fear: 20, thrill: 5, atmosphere: 15, fun: 0 },
    activeSkill: { trigger: 'on_scare', bonus: 0.15, chance: 0.3 },
    themeTags: ['haunted_house', 'school_back_mountain'],
    artProfileId: 'ghost_maiden_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  gangsi: {
    id: 'gangsi',
    nameKey: 'ghost_gangsi',
    originKey: 'ghost_origin_china',
    grade: 'B',
    fearTypes: ['physical'],
    baseFear: 28,
    hireCost: 500,
    skillKey: 'ghost_gangsi_skill',
    quoteKey: 'ghost_gangsi_quote',
    fatiguedQuoteKey: 'ghost_gangsi_fatigued',
    image: '/images/ghost/jiangshi-idle.webp',
    hideImage: '/images/ghost/jiangshi-hide.webp',
    scareImage: '/images/ghost/jiangshi-scare.webp',
    statBonus: { fear: 15, thrill: 20, atmosphere: 5, fun: 10 },
    activeSkill: { trigger: 'on_chain', bonus: 0.25, chance: 0.4 },
    themeTags: ['haunted_house', 'abandoned_hospital'],
    artProfileId: 'ghost_gangsi_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  vampire: {
    id: 'vampire',
    nameKey: 'ghost_vampire',
    originKey: 'ghost_origin_romania',
    grade: 'A',
    fearTypes: ['visual', 'psychological'],
    baseFear: 55,
    hireCost: 2000,
    skillKey: 'ghost_vampire_skill',
    quoteKey: 'ghost_vampire_quote',
    fatiguedQuoteKey: 'ghost_vampire_fatigued',
    image: '/images/ghost/vampire-idle.webp',
    hideImage: '/images/ghost/vampire-hide.webp',
    scareImage: '/images/ghost/vampire-scare.webp',
    statBonus: { fear: 10, thrill: 10, atmosphere: 20, fun: 15 },
    activeSkill: { trigger: 'on_scare', bonus: 0.2, chance: 0.25 },
    themeTags: ['haunted_house'],
    artProfileId: 'ghost_vampire_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  kappa: {
    id: 'kappa',
    nameKey: 'ghost_kappa',
    originKey: 'ghost_origin_japan',
    grade: 'C',
    fearTypes: ['visual'],
    baseFear: 15,
    hireCost: 100,
    skillKey: 'ghost_kappa_skill',
    quoteKey: 'ghost_kappa_quote',
    fatiguedQuoteKey: 'ghost_kappa_fatigued',
    image: '/images/ghost/kappa-idle.webp',
    hideImage: '/images/ghost/kappa-hide.webp',
    scareImage: '/images/ghost/kappa-scare.webp',
    statBonus: { fear: 5, thrill: 15, atmosphere: 10, fun: 25 },
    activeSkill: { trigger: 'on_first_visit', bonus: 0.3, chance: 0.5 },
    themeTags: ['haunted_house'],
    artProfileId: 'ghost_kappa_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  pierrot: {
    id: 'pierrot',
    nameKey: 'ghost_pierrot',
    originKey: 'ghost_origin_usa',
    grade: 'C',
    fearTypes: ['visual'],
    baseFear: 12,
    hireCost: 100,
    skillKey: 'ghost_pierrot_skill',
    quoteKey: 'ghost_pierrot_quote',
    fatiguedQuoteKey: 'ghost_pierrot_fatigued',
    image: '/images/ghost/clown-idle.webp',
    hideImage: '/images/ghost/clown-hide.webp',
    scareImage: '/images/ghost/clown-scare.webp',
    statBonus: { fear: 10, thrill: 25, atmosphere: 5, fun: 30 },
    activeSkill: { trigger: 'on_scare', bonus: 0.1, chance: 0.5 },
    themeTags: ['school_back_mountain'],
    artProfileId: 'ghost_pierrot_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  banshee: {
    id: 'banshee',
    nameKey: 'ghost_banshee',
    originKey: 'ghost_origin_ireland',
    grade: 'B',
    fearTypes: ['auditory'],
    baseFear: 32,
    hireCost: 500,
    skillKey: 'ghost_banshee_skill',
    quoteKey: 'ghost_banshee_quote',
    fatiguedQuoteKey: 'ghost_banshee_fatigued',
    image: '/images/ghost/banshee-idle.webp',
    hideImage: '/images/ghost/banshee-hide.webp',
    scareImage: '/images/ghost/banshee-scare.webp',
    statBonus: { fear: 25, thrill: 10, atmosphere: 20, fun: 0 },
    activeSkill: { trigger: 'on_low_condition', bonus: 0.35, chance: 0.6 },
    themeTags: ['haunted_house', 'school_back_mountain'],
    artProfileId: 'ghost_banshee_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  mummy: {
    id: 'mummy',
    nameKey: 'ghost_mummy',
    originKey: 'ghost_origin_egypt',
    grade: 'A',
    fearTypes: ['physical', 'visual'],
    baseFear: 48,
    hireCost: 1500,
    skillKey: 'ghost_mummy_skill',
    quoteKey: 'ghost_mummy_quote',
    fatiguedQuoteKey: 'ghost_mummy_fatigued',
    image: '/images/ghost/mummy-idle.webp',
    hideImage: '/images/ghost/mummy-hide.webp',
    scareImage: '/images/ghost/mummy-scare.webp',
    statBonus: { fear: 20, thrill: 15, atmosphere: 15, fun: 5 },
    activeSkill: { trigger: 'on_high_fatigue', bonus: 0.3, chance: 0.5 },
    themeTags: ['abandoned_hospital'],
    artProfileId: 'ghost_mummy_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },
  werewolf: {
    id: 'werewolf',
    nameKey: 'ghost_werewolf',
    originKey: 'ghost_origin_europe',
    grade: 'S',
    fearTypes: ['physical', 'auditory'],
    baseFear: 70,
    hireCost: 5000,
    skillKey: 'ghost_werewolf_skill',
    quoteKey: 'ghost_werewolf_quote',
    fatiguedQuoteKey: 'ghost_werewolf_fatigued',
    image: '/images/ghost/werewolf-idle.webp',
    hideImage: '/images/ghost/werewolf-hide.webp',
    scareImage: '/images/ghost/werewolf-scare.webp',
    statBonus: { fear: 25, thrill: 20, atmosphere: 10, fun: 5 },
    activeSkill: { trigger: 'on_chain', bonus: 0.3, chance: 0.35 },
    themeTags: ['haunted_house', 'school_back_mountain'],
    artProfileId: 'ghost_werewolf_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
  },

  // === Professional Ghosts ===
  grim_reaper: {
    id: 'grim_reaper',
    nameKey: 'ghost_grim_reaper',
    originKey: 'ghost_origin_underworld',
    grade: 'S',
    fearTypes: ['visual', 'psychological', 'physical', 'auditory'],
    baseFear: 120,
    hireCost: 50000,
    skillKey: 'ghost_grim_reaper_skill',
    quoteKey: 'ghost_grim_reaper_quote',
    fatiguedQuoteKey: 'ghost_grim_reaper_fatigued',
    image: '/images/ghost/grim-reaper-idle.webp',
    hideImage: '/images/ghost/grim-reaper-hide.webp',
    scareImage: '/images/ghost/grim-reaper-scare.webp',
    statBonus: { fear: 35, thrill: 30, atmosphere: 25, fun: 10 },
    activeSkill: { trigger: 'on_scare', bonus: 0.4, chance: 0.3 },
    themeTags: ['abandoned_hospital'],
    artProfileId: 'ghost_reaper_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
    isPro: true,
    startingSkillLevel: 3,
    uniqueAbilityKey: 'ghost_grim_reaper_unique',
  },
  blood_count: {
    id: 'blood_count',
    nameKey: 'ghost_blood_count',
    originKey: 'ghost_origin_transylvania',
    grade: 'S',
    fearTypes: ['visual', 'psychological'],
    baseFear: 90,
    hireCost: 20000,
    skillKey: 'ghost_blood_count_skill',
    quoteKey: 'ghost_blood_count_quote',
    fatiguedQuoteKey: 'ghost_blood_count_fatigued',
    image: '/images/ghost/blood-count-idle.webp',
    hideImage: '/images/ghost/blood-count-hide.webp',
    scareImage: '/images/ghost/blood-count-scare.webp',
    statBonus: { fear: 20, thrill: 15, atmosphere: 30, fun: 20 },
    activeSkill: { trigger: 'on_chain', bonus: 0.35, chance: 0.45 },
    themeTags: ['haunted_house'],
    artProfileId: 'ghost_blood_count_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
    isPro: true,
    startingSkillLevel: 3,
    uniqueAbilityKey: 'ghost_blood_count_unique',
  },
  well_spirit: {
    id: 'well_spirit',
    nameKey: 'ghost_well_spirit',
    originKey: 'ghost_origin_japan',
    grade: 'S',
    fearTypes: ['psychological'],
    baseFear: 95,
    hireCost: 15000,
    skillKey: 'ghost_well_spirit_skill',
    quoteKey: 'ghost_well_spirit_quote',
    fatiguedQuoteKey: 'ghost_well_spirit_fatigued',
    image: '/images/ghost/well-spirit-idle.webp',
    hideImage: '/images/ghost/well-spirit-hide.webp',
    scareImage: '/images/ghost/well-spirit-scare.webp',
    statBonus: { fear: 30, thrill: 10, atmosphere: 30, fun: 0 },
    activeSkill: { trigger: 'on_low_condition', bonus: 0.5, chance: 0.5 },
    themeTags: ['haunted_house', 'school_back_mountain'],
    artProfileId: 'ghost_well_spirit_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
    isPro: true,
    startingSkillLevel: 3,
    uniqueAbilityKey: 'ghost_well_spirit_unique',
  },
  nightmare_specter: {
    id: 'nightmare_specter',
    nameKey: 'ghost_nightmare_specter',
    originKey: 'ghost_origin_dreamworld',
    grade: 'S',
    fearTypes: ['physical', 'psychological'],
    baseFear: 100,
    hireCost: 25000,
    skillKey: 'ghost_nightmare_specter_skill',
    quoteKey: 'ghost_nightmare_specter_quote',
    fatiguedQuoteKey: 'ghost_nightmare_specter_fatigued',
    image: '/images/ghost/nightmare-idle.webp',
    hideImage: '/images/ghost/nightmare-hide.webp',
    scareImage: '/images/ghost/nightmare-scare.webp',
    statBonus: { fear: 30, thrill: 25, atmosphere: 20, fun: 5 },
    activeSkill: { trigger: 'on_high_fatigue', bonus: 0.45, chance: 0.4 },
    themeTags: ['abandoned_hospital'],
    artProfileId: 'ghost_nightmare_specter_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
    isPro: true,
    startingSkillLevel: 3,
    uniqueAbilityKey: 'ghost_nightmare_specter_unique',
  },
  shadow_stalker: {
    id: 'shadow_stalker',
    nameKey: 'ghost_shadow_stalker',
    originKey: 'ghost_origin_void',
    grade: 'S',
    fearTypes: ['psychological'],
    baseFear: 110,
    hireCost: 30000,
    skillKey: 'ghost_shadow_stalker_skill',
    quoteKey: 'ghost_shadow_stalker_quote',
    fatiguedQuoteKey: 'ghost_shadow_stalker_fatigued',
    image: '/images/ghost/shadow-stalker-idle.webp',
    hideImage: '/images/ghost/shadow-stalker-hide.webp',
    scareImage: '/images/ghost/shadow-stalker-scare.webp',
    statBonus: { fear: 35, thrill: 15, atmosphere: 25, fun: 0 },
    activeSkill: { trigger: 'on_first_visit', bonus: 0.5, chance: 0.35 },
    themeTags: ['school_back_mountain'],
    artProfileId: 'ghost_shadow_stalker_outline_navy',
    artNotes: 'Keep existing style, black outline, solid navy background for clean removal.',
    isPro: true,
    startingSkillLevel: 3,
    uniqueAbilityKey: 'ghost_shadow_stalker_unique',
  },
};

/** Regular ghost IDs (non-pro) */
export const REGULAR_GHOST_IDS: GhostId[] = [
  'jinaeryeong', 'gangsi', 'vampire', 'kappa',
  'pierrot', 'banshee', 'mummy', 'werewolf',
];

/** Professional ghost IDs */
export const PRO_GHOST_IDS: GhostId[] = [
  'grim_reaper', 'blood_count', 'well_spirit', 'nightmare_specter', 'shadow_stalker',
];

export const ALL_GHOST_IDS = Object.keys(GHOST_DEFS) as GhostId[];
